import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../src/contexts/AuthContext';
import { Lock, Mail, AlertCircle, Loader2, ArrowRight, KeyRound, CheckCircle2, X } from 'lucide-react';
import { logAction } from '../src/lib/logger';
import { supabase } from '../src/lib/supabase';
import { isContaSuspensa } from '../src/lib/statusConta';
import MaintenanceScreen from '../src/components/MaintenanceScreen';
import { propsFundoModal } from '../src/lib/backdropModal';

const LoginView = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    // Conta inativada: o login é recusado, mas o usuário só vê "manutenção"
    const [manutencaoEm, setManutencaoEm] = useState<Date | null>(null);
    const { signIn } = useAuth();
    const navigate = useNavigate();

    // Forgot password modal state
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSuccess, setForgotSuccess] = useState(false);
    const [forgotError, setForgotError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const { data, error } = await signIn(email, password);

            if (error) {
                if (isContaSuspensa(error)) {
                    setManutencaoEm(new Date());
                } else if (error.message === 'Invalid login credentials') {
                    setError('Os dados de acesso estão incorretos');
                } else {
                    setError(error.message || 'Erro ao fazer login');
                }
            } else {
                if (data?.user) {
                    await logAction(data.user.id, 'LOGIN', { email: data.user.email });
                }
                navigate('/');
            }
        } catch (err) {
            setError('Ocorreu um erro inesperado.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setForgotError(null);
        setForgotLoading(true);

        try {
            const { data, error } = await supabase.functions.invoke('request-password-reset', {
                body: { email: forgotEmail }
            });

            if (error) throw error;
            if (data && data.success === false) {
                throw new Error(data.error || 'Erro ao enviar solicitação.');
            }

            setForgotSuccess(true);
        } catch (err: any) {
            setForgotError(err.message || 'Erro ao enviar solicitação. Tente novamente.');
        } finally {
            setForgotLoading(false);
        }
    };

    const openForgotModal = () => {
        setForgotEmail(email); // Pre-fill with login email if available
        setForgotError(null);
        setForgotSuccess(false);
        setShowForgotModal(true);
    };

    const closeForgotModal = () => {
        setShowForgotModal(false);
        setForgotEmail('');
        setForgotError(null);
        setForgotSuccess(false);
    };

    if (manutencaoEm) {
        return (
            <MaintenanceScreen
                ultimaVerificacao={manutencaoEm}
                acao={{ label: 'Voltar', onClick: () => { setManutencaoEm(null); setPassword(''); } }}
            />
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="p-8 sm:p-12">
                    {/* Logo */}
                    <div className="flex justify-center mb-8">
                        <img src="/vigorhub_logo_transparent.png" alt="VigorHub Logo" className="h-20 object-contain" />
                    </div>

                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-gray-900">Bem-vindo de volta!</h1>
                        <p className="text-gray-500 mt-2">Acesse sua conta corporativa para continuar.</p>
                    </div>

                    {error && (
                        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3 text-sm animate-fade-in">
                            <AlertCircle size={18} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Corporativo</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                    placeholder="seu.nome@vigorenergy.com.br"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Senha</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-end">
                            <button
                                type="button"
                                onClick={openForgotModal}
                                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                            >
                                Esqueceu a senha?
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center items-center gap-2 py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-primary/25 text-sm font-bold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-70 disabled:cursor-not-allowed transition-all transform active:scale-[0.98]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Entrando...
                                </>
                            ) : (
                                <>
                                    Entrar
                                    <ArrowRight size={18} />
                                </>
                            )}
                        </button>
                    </form>
                </div>
                <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 text-center">
                    <p className="text-xs text-gray-500">
                        Acesso restrito a colaboradores Vigor Energy. <br />
                        <span className="font-semibold text-primary">@vigorenergy.com.br</span>
                    </p>
                </div>
            </div>

            {/* Forgot Password Modal */}
            {showForgotModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => closeForgotModal())}>
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden transform transition-all" onClick={e => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <div className="flex items-center gap-2">
                                <KeyRound size={20} className="text-primary" />
                                <h3 className="font-bold text-lg text-gray-800">Recuperar Senha</h3>
                            </div>
                            <button onClick={closeForgotModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        {forgotSuccess ? (
                            <div className="p-8 text-center space-y-4">
                                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                                    <CheckCircle2 size={32} className="text-green-600" />
                                </div>
                                <h4 className="text-lg font-bold text-gray-900">Solicitação Enviada!</h4>
                                <p className="text-sm text-gray-500">
                                    Sua solicitação foi enviada aos administradores. Em breve, você receberá suas novas credenciais de acesso.
                                </p>
                                <button
                                    onClick={closeForgotModal}
                                    className="mt-4 w-full py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors"
                                >
                                    Entendi
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleForgotPassword} className="p-6 space-y-5">
                                <p className="text-sm text-gray-500">
                                    Informe seu email corporativo. Os administradores serão notificados e redefinirão sua senha.
                                </p>

                                {forgotError && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3 text-sm animate-fade-in">
                                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                                        <span>{forgotError}</span>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Corporativo</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail size={18} className="text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            required
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                            placeholder="seu.nome@vigorenergy.com.br"
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={closeForgotModal}
                                        className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={forgotLoading}
                                        className="flex-1 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                                    >
                                        {forgotLoading ? (
                                            <Loader2 size={18} className="animate-spin" />
                                        ) : (
                                            <KeyRound size={18} />
                                        )}
                                        Solicitar
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginView;
